"use client"

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react"
import { login as apiLogin, logout as apiLogout, getStoredUser, isAuthenticated } from "@/lib/api"
import type { AuthUser } from "@/lib/types"

interface MfaRequired {
  mfa_required: true
  user_id: number
}

interface AuthContextType {
  user: AuthUser | null
  isLoading: boolean
  isLoggedIn: boolean
  login: (email: string, password: string) => Promise<MfaRequired | void>
  loginComplete: (user: AuthUser) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const stored = getStoredUser()
    if (stored && isAuthenticated()) {
      setUser(stored)
    }
    setIsLoading(false)
  }, [])

  const handleLogin = useCallback(async (email: string, password: string) => {
    const result = await apiLogin(email, password)
    if (result.mfa_required) {
      return { mfa_required: true as const, user_id: result.user_id! }
    }
    setUser(result.user)
  }, [])

  const handleLoginComplete = useCallback((user: AuthUser) => {
    setUser(user)
  }, [])

  const handleLogout = useCallback(() => {
    // Navigate first to avoid React DOM reconciliation errors
    apiLogout()
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isLoggedIn: !!user,
        login: handleLogin,
        loginComplete: handleLoginComplete,
        logout: handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
